/**
 * BaseNode — shared chrome for every agent-builder canvas node.
 *
 * Each typed node component (TriggerNode, RagNode, …) is a thin wrapper that
 * passes its backend node-type constant; BaseNode renders the consistent card
 * chrome (header accent, label, params summary) and the ReactFlow connection
 * handles. The execution status (from the live SSE overlay) is read from the
 * node `data` so a running/completed/failed node lights up in place.
 */

import type { ReactNode } from 'react';
import { Handle, Position, type NodeProps } from 'reactflow';
import { NODE_TYPE_META, type NodeType } from '../../lib/agents';
import type { CanvasNodeData } from '../../stores/canvasStore';

/** Per-node execution status pushed onto node data by the ExecutionOverlay. */
export type NodeRunStatus = 'idle' | 'running' | 'completed' | 'failed';

/** Node data augmented with the live run status (optional). */
export interface BaseNodeData extends CanvasNodeData {
  runStatus?: NodeRunStatus;
}

const STATUS_RING: Record<NodeRunStatus, string> = {
  idle: 'ring-0',
  running: 'ring-2 ring-sky-400 animate-pulse',
  completed: 'ring-2 ring-emerald-400',
  failed: 'ring-2 ring-rose-400',
};

const STATUS_LABEL: Record<NodeRunStatus, string> = {
  idle: '',
  running: 'Running…',
  completed: 'Completed',
  failed: 'Failed',
};

interface BaseNodeProps extends NodeProps<BaseNodeData> {
  /** The backend node-type constant this node renders. */
  nodeType: NodeType;
}

export function BaseNode({ data, selected, nodeType }: BaseNodeProps): ReactNode {
  const meta = NODE_TYPE_META[nodeType];
  const status: NodeRunStatus = data.runStatus ?? 'idle';

  return (
    <div
      data-testid={`canvas-node-${nodeType}`}
      data-node-type={nodeType}
      data-run-status={status}
      className={`w-56 rounded-lg border-2 shadow-sm bg-white ${meta.accent} ${
        STATUS_RING[status]
      } ${selected ? 'outline outline-2 outline-navy-500' : ''}`}
    >
      {meta.hasInput && (
        <Handle
          type="target"
          position={Position.Left}
          className="!w-3 !h-3 !bg-navy-500"
        />
      )}

      <div className="px-3 py-2 border-b border-black/10 flex items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-navy-700">
          {meta.label}
        </span>
        {status !== 'idle' && (
          <span
            className="text-[10px] font-medium text-navy-600"
            data-testid="node-status-label"
          >
            {STATUS_LABEL[status]}
          </span>
        )}
      </div>

      <div className="px-3 py-2">
        <p className="text-sm font-medium text-navy-900 truncate">{data.name}</p>
        <p className="mt-0.5 text-[11px] text-steel-500 line-clamp-2">
          {meta.description}
        </p>
      </div>

      {meta.hasOutput && (
        <Handle
          type="source"
          position={Position.Right}
          className="!w-3 !h-3 !bg-navy-500"
        />
      )}
    </div>
  );
}
